<template>
  <div
    class="ca-slider-simulator-card"
    :class="{ 'ca-slider-simulator-card--loading': isImageLoading }"
    :aria-busy="isImageLoading || undefined"
  >
    <CaSliderCardSkeleton
      v-if="isImageLoading"
      variant="simulator"
      :button-count="buttons.length || 6"
    />
    <div class="ca-slider-simulator-card__info">
      <span v-if="label" class="ca-slider-simulator-card__label caption-medium">
        {{ label }}
      </span>
      <component
        :is="normalizeHeadingTag(titleHeadingType, 'p')"
        class="ca-slider-simulator-card__title heading-bold"
      >
        {{ title }}
      </component>
      <BaseButton
        :label="simulatorLabel"
        class="inverted primary ca-slider-simulator-card__cta ca-slider-simulator-card__cta--desktop"
        :class="{ 'ca-slider-simulator-card__cta--pulse': selectedButton !== null }"
        @pointerdown.stop
        @click="handleSimulatorClick"
      >
        <template #leadingIcon>
          <ChevronRight />
        </template>
      </BaseButton>
    </div>
    <div class="ca-slider-simulator-card__image">
      <div
        v-if="imageSrc"
        class="ca-slider-simulator-card__media"
      >
        <img
          :src="imageSrc"
          :alt="imageAlt"
          class="ca-slider-simulator-card__img"
          aria-hidden="true"
          @load="handleImageSettled"
          @error="handleImageSettled"
        />
      </div>
      <div class="ca-slider-simulator-card__overlay">
        <div class="ca-slider-simulator-card__buttons" :class="{ 'has-selection': selectedButton !== null }">
          <BaseButton
            v-for="(btn, idx) in buttons"
            :key="idx"
            :label="btn.label"
            :class="selectedButton === idx ? 'inverted primary' : 'inverted secondary'"
            @pointerdown.stop
            @click="selectButton(idx, btn)"
          />
        </div>
        <BaseButton
          :label="simulatorLabel"
          class="inverted primary ca-slider-simulator-card__cta ca-slider-simulator-card__cta--mobile"
          :class="{ 'ca-slider-simulator-card__cta--pulse': selectedButton !== null }"
          @pointerdown.stop
          @click="handleSimulatorClick"
        >
          <template #leadingIcon>
            <ChevronRight />
          </template>
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, watch } from "vue";
import { BaseButton } from "ca-online-design-system";
import CaSliderCardSkeleton from "./CaSliderCardSkeleton.vue";
import ChevronRight from "@assets/chevron-right.svg";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaSliderSimulatorCard",
});

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
  titleHeadingType: {
    type: String,
    default: "Paragraph",
  },
  imageSrc: {
    type: String,
    default: "",
  },
  imageAlt: {
    type: String,
    default: "",
  },
  simulatorLabel: {
    type: String,
    default: "Simulador",
  },
  simulatorUrl: {
    type: String,
    default: "",
  },
  simulatorTarget: {
    type: String,
    default: "_self",
  },
  buttons: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["action", "button-select"]);

const selectedButton = ref(null);
const ctaPulse = ref(false);
const isImageLoading = ref(Boolean(props.imageSrc));

watch(
  () => props.imageSrc,
  (imageSrc) => {
    isImageLoading.value = Boolean(imageSrc);
  },
);

function handleImageSettled() {
  isImageLoading.value = false;
}

function selectButton(idx, btn) {
  if (selectedButton.value === idx) {
    selectedButton.value = null;
    return;
  }
  selectedButton.value = idx;
  ctaPulse.value = false;
  nextTick(() => { ctaPulse.value = true; });
  emit("button-select", btn);
}

function handleSimulatorClick() {
  emit("action");
  if (props.simulatorUrl) {
    window.open(props.simulatorUrl, props.simulatorTarget);
  }
}
</script>

<style scoped lang="scss">
.ca-slider-simulator-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--size-4);
  padding: var(--size-4);
  padding-top: var(--size-3);
  height: 582px;
  width: 100%;
  border-radius: var(--size-5);
  background-color: var(--ca-color-neutrals-on-background-high-40);
  overflow: hidden;

  &--loading {
    > .ca-slider-simulator-card__info,
    > .ca-slider-simulator-card__image {
      visibility: hidden;
    }
  }

  @include ca-bp("sm") {
    flex-direction: row;
    align-items: flex-start;
    gap: var(--size-5);
    padding: var(--size-4);
    height: 344px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--size-4);
    padding: var(--size-4) var(--size-3);
    flex: 1 1 0;
    min-height: 0;

    @include ca-bp("sm") {
      width: 286px;
      justify-content: center;
      height: 100%;
      flex: 0 0 auto;
    }

    @include ca-bp("md") {
      width: 312px;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--size-3) var(--size-4);
    border-radius: 100px;
    background-color: var(--ca-color-neutrals-inverted-10);
    color: var(--ca-color-text-inverted);
    white-space: nowrap;
    text-transform: uppercase;
  }

  &__title {
    width: 100%;
    color: var(--ca-color-text-inverted);
    margin: 0;
    font-family: var(--font-family-heading);
    max-height: 96px;
    overflow: hidden;

    @include ca-bp("sm") {
      flex: 1 0 0;
      min-height: 1px;
      max-height: none;
      overflow: visible;
    }

    @include ca-bp("md") {
      font-size: 32px !important;
      line-height: 40px !important;
      letter-spacing: 0.384px !important;
    }
  }

  &__cta {
    flex-shrink: 0;

    &--pulse {
      animation: cta-pulse 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;

      &:hover {
        animation: none;
      }
    }

    &--desktop {
      display: none;

      @include ca-bp("sm") {
        display: flex;
      }
    }

    &--mobile {
      display: flex;
      width: 100%;
      margin-top: var(--size-3);

      @include ca-bp("sm") {
        display: none;
      }
    }
  }

  &__image {
    position: relative;
    height: 360px;
    flex-shrink: 0;
    width: 100%;
    border-radius: var(--size-4);
    overflow: hidden;

    @include ca-bp("sm") {
      height: 312px;
      min-width: 1px;
      align-self: stretch;
      width: auto;
      flex: 1 0 0;
    }
  }

  &__media {
    position: absolute;
    inset: 0;
    border-radius: var(--size-4);
    overflow: hidden;
  }

  &__img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--size-4);
    pointer-events: none;
  }

  &__overlay {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: var(--size-4);
    border-radius: var(--size-4);
    background: linear-gradient(
      to bottom,
      rgba(100, 111, 118, 0) 0.13%,
      rgba(0, 0, 0, 0.6) 45.1%
    );
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;
    gap: var(--size-3);
    width: 100%;

    :deep(.base-button) {
      flex: 1 1 calc(50% - var(--size-2));
      min-width: 0;
      padding-right: var(--size-3);
      padding-left: var(--size-3);
    }

    @include ca-bp("sm") {
      :deep(.base-button) {
        flex-basis: calc(33.333333% - var(--size-3));
      }
    }

    &.has-selection :deep(.base-button.inverted.primary:hover),
    &.has-selection :deep(.base-button.inverted.primary:active) {
      background-color: white !important;
    }
  }

}

@keyframes cta-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.55);
  }
  75%,
  100% {
    box-shadow: 0 0 0 14px rgba(255, 255, 255, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ca-slider-simulator-card__cta--pulse {
    animation: none;
  }
}
</style>
